<template>
  <div>
    <ValidationProvider
      ref="provider"
      mode="passive"
      :name="$t(`inputs.headings.customer_details`)"
      :vid="validationPath"
      :rules="validationRules"
      v-slot="{ errors }"
      slim
    >
      <b-form-invalid-feedback :state="compoundState(errors)">
        {{ compoundFeedback(errors) }}
      </b-form-invalid-feedback>
    </ValidationProvider>
    <input-contact
      v-e2e:jobCreateInputContact
      v-model="model.contact"
      :type="type"
      :extra-fields="true"
      :readonly="readonly"
      :validation-path="validationPath"
      :validation-context="validationContext"
    />
    <h5 class="text-muted">{{ $t('inputs.customer.shipping-address') }}</h5>
    <div>
      <input-address
        v-e2e:jobCreateInputAddress
        v-model="model.shipping_address"
        :type="type"
        :readonly="readonly"
        :validation-path="`${validationPath}.shipping_address`"
        :validation-context="validationContext"
      />
      <b-form-row v-if="allowCustomCoordinates && !readonly">
        <b-col cols="12">
          <b-form-checkbox
            v-e2e:jobCreateCustomCoordinates
            v-model="useCustomCoordinates"
            @change="emit"
            class="h5 text-muted"
          >
            {{ $t('inputs.customer.custom-coordinates') }}
          </b-form-checkbox>
        </b-col>
        <template v-if="useCustomCoordinates">
          <b-form-group :label="$t('inputs.customer.latitude')" class="col">
            <b-form-input
              v-e2e:jobCreateLatitude
              v-model="model.shipping_address.latitude"
              :readonly="readonly"
            />
          </b-form-group>
          <b-form-group :label="$t('inputs.customer.longitude')" class="col">
            <b-form-input
              v-e2e:jobCreateLongitude
              v-model="model.shipping_address.longitude"
              :readonly="readonly"
            />
          </b-form-group>
          <b-form-group class="col">
            <template #label>&nbsp;</template>
            <b-button
              v-e2e:jobCreateLocateButton
              block
              variant="outline-primary"
              @click="openMap"
            >
              {{ $t('inputs.customer.locate-btn') }}
              <i class="material-icons">place</i>
            </b-button>
          </b-form-group>
          <b-modal size="lg" ref="modalMap" title="Map" @ok="useMapOptions">
            <b-row class="mb-2">
              <b-col md="9" class="d-flex align-items-baseline">
                <span>{{ $t('inputs.customer.location') }}:</span>
                <b-form-input
                  class="ml-2"
                  placeholder="Address"
                  v-model="map.location"
                  @input="locationInput"
                  :state="map.addressFound"
                />
              </b-col>
              <b-col md="3">
                <b-button
                  block
                  @click="locate"
                  :disabled="!map.addressChanged || !map.location"
                  class="text-nowrap"
                >
                  <b-spinner v-if="map.geolocating" small class="mr-1" />
                  {{ $t('inputs.customer.search') }}
                </b-button>
              </b-col>
            </b-row>
            <div class="gid-map">
              <l-map
                ref="map"
                :zoom="map.zoom"
                :center="map.center"
                :options="{ attributionControl: false }"
              >
                <l-tile-layer url="https://{s}.tile.osm.org/{z}/{x}/{y}.png" />
                <l-marker
                  :lat-lng.sync="map.marker"
                  @update:latLng="onMarkerDrag"
                  :draggable="true"
                  :options="{ autoPan: true }"
                  @dragend="onMarkerDragEnd"
                />
              </l-map>
            </div>
          </b-modal>
        </template>
      </b-form-row>
    </div>
    <template v-if="showBillingFields">
      <b-form-checkbox
        v-model="useBillingContact"
        @change="emit"
        class="h5 text-muted"
        :disabled="true"
      >
        {{ $t('inputs.customer.billing-contact') }}
      </b-form-checkbox>
      <b-card v-if="useBillingContact" class="mb-2">
        <input-contact
          v-model="model.billing_contact"
          :type="type"
          :readonly="readonly"
          :validation-path="`${validationPath}.billing_contact`"
          :validation-context="validationContext"
        />
      </b-card>
      <b-form-checkbox
        v-model="useBillingAddress"
        @change="emit"
        class="h5 text-muted"
        :disabled="readonly"
      >
        {{ $t('inputs.customer.billing-address') }}
      </b-form-checkbox>
      <b-card v-if="useBillingAddress">
        <b-row>
          <b-form-group
            :label="$t('inputs.customer.first-name')"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:customerContactBillingFirstName
              v-model="model.contact.billing_first_name"
              :readonly="readonly"
            />
          </b-form-group>
          <b-form-group
            :label="$t('inputs.customer.last-name')"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:customerContactBillingLastName
              v-model="model.contact.billing_last_name"
              :readonly="readonly"
            />
          </b-form-group>
        </b-row>
        <b-row>
          <b-form-group :label="$t('inputs.customer.company')" class="col-md-6">
            <b-form-input
              v-e2e:customerContactCompanyName
              v-model="model.contact.billing_company_name"
              :readonly="readonly"
            />
          </b-form-group>
          <validating-group
            :label="$t('email')"
            :validation-context="validationContext"
            :validation-path="`${validationPath}.email`"
            :rules="{ email: true }"
            v-slot="{ clearErrors, state }"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:customerContactEmail
              v-model="model.contact.email_for_invoices"
              @input="clearErrors"
              :readonly="readonly"
              :state="state"
            />
          </validating-group>
        </b-row>
        <input-address
          v-model="model.billing_address"
          :type="type"
          :readonly="readonly"
          :validation-path="`${validationPath}.billing_address`"
          :validation-context="validationContext"
        />
      </b-card>
      <b-form-checkbox
        v-model="model.contactPerson.use"
        @change="emit"
        class="h5 text-muted"
        :disabled="readonly"
      >
        {{ $t('inputs.contact_person.use') }}
      </b-form-checkbox>
      <b-card v-if="model.contactPerson.use">
        <b-form-checkbox
          v-model="model.contactPerson.use_for_email"
          :disabled="readonly"
        >
          {{ $t('inputs.contact_person.use_for_email') }}
        </b-form-checkbox>
        <p class="w-75">{{ $t('inputs.contact_person.explanation') }}</p>

        <b-row v-if="quoteTargetOptions">
          <b-form-group
            :label="$t('inputs.customer.quote-target')"
            class="col-md-6"
          >
            <multiselect
              v-model="model.contactPerson.quote_target"
              :readonly="readonly"
              :show-labels="false"
              :options="quoteTargetOptions"
              :disabled="readonly"
            />
          </b-form-group>
        </b-row>

        <b-row>
          <b-form-group
            :label="$t('inputs.customer.first-name')"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:contactPersonFirstName
              v-model="model.contactPerson.first_name"
              :readonly="readonly"
            />
          </b-form-group>
          <b-form-group
            :label="$t('inputs.customer.last-name')"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:contactPersonLastName
              v-model="model.contactPerson.last_name"
              :readonly="readonly"
            />
          </b-form-group>
        </b-row>
        <b-row>
          <validating-group
            :label="$t('inputs.customer.phone')"
            :validation-context="validationContext"
            :validation-path="`${validationPath}.phone`"
            :rules="{ phone: true }"
            v-slot="{ clearErrors, state }"
            class="col-sm-6"
          >
            <b-input-group>
              <template #prepend>
                <b-dropdown variant="outline-primary">
                  <template #button-content>
                    <span class="dialing-country__selected">
                      <country-flag
                        :country="countryPhone.code"
                        class="dialing-country__selected__flag"
                      />
                      <span class="dialing-country__selected__prefix">
                        ( {{ countryPhone.dialing }} )
                      </span>
                    </span>
                  </template>
                  <b-dropdown-item
                    @click="selectCountryPhone(country)"
                    v-for="country in countriesTier1"
                    :key="country.name"
                    :value="country.code"
                  >
                    <div>
                      <country-flag :country="country.code" />
                      <span class="ml-2">
                        {{ country.name }} ( {{ country.dialing }} )
                      </span>
                    </div>
                  </b-dropdown-item>

                  <b-dropdown-divider></b-dropdown-divider>

                  <b-dropdown-item
                    @click="selectCountryPhone(country)"
                    v-for="country in countriesTier2"
                    :key="country.name"
                    :value="country.code"
                  >
                    <div>
                      <country-flag :country="country.code" />
                      <span class="ml-2">
                        {{ country.name }} ( {{ country.dialing }} )
                      </span>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
              <b-form-input
                v-e2e:customerContactPhone
                v-model="contactPersonPhone"
                @input="
                  modelChanged();
                  clearErrors();
                "
                :readonly="readonly"
                :state="state"
              />
            </b-input-group>
          </validating-group>
          <validating-group
            :label="$t('email')"
            :validation-context="validationContext"
            :validation-path="`${validationPath}.email`"
            :rules="{ email: true }"
            v-slot="{ clearErrors, state }"
            class="col-md-6"
          >
            <b-form-input
              v-e2e:contactPersonEmail
              v-model="model.contactPerson.email"
              @input="clearErrors"
              :readonly="readonly"
              :state="state"
            />
          </validating-group>
        </b-row>
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import BaseInput from './mixins/BaseInput';
import ValidatingInput from '../mixins/ValidatingInput';
import Address from './Address.vue';
import Contact from './Contact.vue';
import { CUSTOMER_DETAILS } from './_types';
import { QuoteTargetEnum } from '@gid/models';
import CountryFlag from 'vue-country-flag';
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';

export default {
  mixins: [BaseInput, ValidatingInput],
  components: {
    'input-address': Address,
    'input-contact': Contact,
    LMap,
    LTileLayer,
    LMarker,
    CountryFlag,
    Multiselect,
  },
  props: {
    showBillingFields: Boolean,
    allowCustomCoordinates: Boolean,
    inputName: {
      type: String,
      default: CUSTOMER_DETAILS,
    },
    contactPerson: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      model: {
        contact: {
          salutation: '',
          first_name: null,
          last_name: null,
          eid: null,
          company_name: null,
          email: null,
          phone: null,
          mobile_phone: null,
          email_for_invoices: null,
          billing_company_name: null,
          billing_first_name: null,
          billing_last_name: null,
        },
        billing_contact: {
          salutation: '',
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          mobile_phone: null,
          company_name: null,
        },
        shipping_address: {
          city: null,
          country_code: null,
          postal_code: null,
          street: null,
          latitude: null,
          longitude: null,
        },
        billing_address: {
          city: null,
          country_code: null,
          postal_code: null,
          street: null,
        },
        contactPerson: {
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          use_for_email: false,
          use: false,
          quote_target: QuoteTargetEnum.CUSTOMER,
        },
      },
      quoteTargetOptions: Object.values(QuoteTargetEnum),
      useBillingContact: false,
      useBillingAddress: false,
      useReceiveContact: false,
      useCustomCoordinates: false,
      map: {
        zoom: 8,
        center: [0, 0],
        marker: [0, 0],
        location: '',
        geolocating: false,
        addressChanged: false,
        addressFound: null,
      },
      countryPhone: {
        name: this.$t('country.germany'),
        code: 'DE',
        dialing: '+49',
      },
      countryMobilePhone: {
        name: this.$t('country.germany'),
        code: 'DE',
        dialing: '+49',
      },
      countries: [
        { name: this.$t('country.germany'), code: 'DE', dialing: '+49' },
        { name: this.$t('country.austria'), code: 'AT', dialing: '+43' },
        { name: this.$t('country.france'), code: 'FR', dialing: '+33' },
        { name: this.$t('country.gb'), code: 'GB', dialing: '+44' },
        { name: this.$t('country.sweden'), code: 'SE', dialing: '+46' },

        { name: this.$t('country.netherlands'), code: 'NL', dialing: '+31' },
        { name: this.$t('country.belgium'), code: 'BE', dialing: '+32' },
        { name: this.$t('country.switzerland'), code: 'CH', dialing: '+41' },
        { name: this.$t('country.denmark'), code: 'DK', dialing: '+45' },
        { name: this.$t('country.poland'), code: 'PL', dialing: '+48' },
        { name: this.$t('country.luxembourg'), code: 'LU', dialing: '+352' },
        { name: this.$t('country.slovenia'), code: 'SI', dialing: '+386' },
        {
          name: this.$t('country.czech-republic'),
          code: 'CZ',
          dialing: '+420',
        },
      ],
    };
  },
  watch: {
    model: {
      deep: true,
      handler() {
        this.emit();
      },
    },
  },
  created() {
    this.init();
  },
  computed: {
    countriesTier1() {
      return this.countries.slice(0, 5);
    },
    countriesTier2() {
      return this.countries.slice(5, this.countries.length);
    },
    contactPersonPhone: {
      get() {
        if (this.model.contactPerson.phone) {
          return this.model.contactPerson.phone.replace(
            this.countryMobilePhone.dialing,
            '',
          );
        }
        return null;
      },
      set(contactPersonPhone) {
        if (contactPersonPhone) {
          this.model.contactPerson.phone = `${this.countryMobilePhone.dialing}${contactPersonPhone}`;
        } else {
          this.model.contactPerson.phone = null;
        }
      },
    },
  },
  methods: {
    init() {
      if (this.value) {
        let newModel = JSON.parse(JSON.stringify(this.value));
        let contact = {};
        [
          'salutation',
          'first_name',
          'last_name',
          'eid',
          'company_name',
          'email',
          'phone',
          'mobile_phone',
          'email_for_invoices',
          'billing_company_name',
          'billing_first_name',
          'billing_last_name',
        ].forEach((prop) => {
          contact[prop] = newModel[prop];
          delete newModel[prop];
        });
        newModel.contact = contact;

        if (this.showBillingFields) {
          // !Disabled for now, as it is broken on database level!
          // this.useBillingContact = Boolean(
          //   newModel.billing_contact && newModel.billing_contact.first_name,
          // );
          if (newModel.billing_address) {
            let billingAddressMatch = true;
            ['city', 'country_code', 'postal_code', 'street'].forEach(
              (prop) => {
                if (newModel.billing_address[prop]) {
                  billingAddressMatch =
                    billingAddressMatch &&
                    newModel.billing_address[prop] ==
                      newModel.shipping_address[prop];
                }
              },
            );
            this.useBillingAddress = !billingAddressMatch;
          }
        }
        newModel.contactPerson = { ...this.contactPerson };
        this.model = newModel;
      }
    },
    emit() {
      this.$nextTick(() => {
        let emitModel = JSON.parse(JSON.stringify(this.model));
        const contact = emitModel.contact;
        delete emitModel.contact;
        emitModel = {
          ...contact,
          ...emitModel,
        };
        if (!this.useCustomCoordinates) {
          delete emitModel.shipping_address.latitude;
          delete emitModel.shipping_address.longitude;
        }
        if (!this.useBillingContact) {
          delete emitModel.billing_contact;
        }
        if (!this.useBillingAddress) {
          delete emitModel.billing_address;
        }
        if (!this.useReceiveContact) {
          delete emitModel.receive_contact;
        }
        if (!emitModel.contactPerson.use) {
          delete emitModel.contactPerson;
        }
        this.commitValue(emitModel);
        this.$refs.provider.validate(emitModel);
      });
    },
    buildSearchAddress() {
      let newLocation = '';
      const sa = this.model.shipping_address;
      if (sa.street) {
        newLocation += sa.street;
      }
      if (sa.postal_code) {
        if (newLocation) newLocation += ', ';
        newLocation += sa.postal_code;
      }
      if (sa.city) {
        if (newLocation) newLocation += ' ';
        newLocation += sa.city;
      }
      if (sa.country) {
        if (newLocation) newLocation += ', ';
        newLocation += sa.country;
      }
      return newLocation;
    },
    openMap() {
      this.map.location = this.buildSearchAddress();
      if (this.map.location) {
        this.locationInput();
      }
      if (
        !this.model.shipping_address.latitude ||
        !this.model.shipping_address.longitude
      ) {
        this.map.center = this.map.marker = [52.52, 13.4];
        this.locate();
      } else {
        this.map.center = this.map.marker = [
          this.model.shipping_address.latitude,
          this.model.shipping_address.longitude,
        ];
      }

      this.$refs.modalMap.show();
      window.requestAnimationFrame(() => {
        this.$refs.map.mapObject.invalidateSize();
      });
    },
    onMarkerDrag(latlng) {
      this.map.marker = [latlng.lat, latlng.lng];
    },
    onMarkerDragEnd() {
      this.map.location = '';
    },
    useMapOptions() {
      this.model.shipping_address.latitude =
        Math.round(this.map.marker.lat * 10000) / 10000;
      this.model.shipping_address.longitude =
        Math.round(this.map.marker.lng * 10000) / 10000;
    },
    locationInput() {
      this.map.addressChanged = true;
      this.map.addressFound = null;
    },
    locate() {
      this.map.addressChanged = false;
      this.map.geolocating = true;
      axios
        .get('https://nominatim.openstreetmap.org/search', {
          params: {
            q: this.map.location,
            format: 'json',
            limit: 1,
          },
        })
        .then((response) => {
          if (response.data && response.data.length) {
            this.map.center = this.map.marker = [
              response.data[0].lat,
              response.data[0].lon,
            ];
            this.map.addressFound = true;
          } else {
            this.map.addressFound = false;
          }
          this.map.geolocating = false;
        })
        .catch(() => {
          this.map.geolocating = false;
        });
    },
    selectCountryPhone(country) {
      this.countryPhone = country;
      this.model.contactPerson.phone = '';
    },
  },
};
</script>
<style lang="scss" scoped>
.input-group-prepend {
  .dropdown.b-dropdown.btn-group {
    height: 38px;
  }
  button {
    &:after {
      content: '';
      display: block;
      padding-top: 2px;
    }
    .normal-flag {
      margin-top: -8px;
    }
  }
}

.dropdown-item {
  div {
    span.flag.normal-flag {
      margin: -13px -15px;
    }
  }
}

.dialing-country__selected__prefix {
  position: relative;
  top: -4.5px;
  left: 4.5px;
  padding-right: 4.5px;
}
</style>
